import { AppView } from '../../types';

// ==========================================
// DATA GOVERNANCE 2.0 & DATA OS TYPES
// ==========================================

export type DataDomainId =
  | 'reference-data'
  | 'financial-market-data'
  | 'fx'
  | 'transfer'
  | 'salary-payroll'
  | 'tax'
  | 'loans-mortgages'
  | 'wealth'
  | 'universal-calculators'
  | 'identity'
  | 'accounts'
  | 'billing'
  | 'revenue'
  | 'advertising'
  | 'affiliate'
  | 'growth'
  | 'analytics'
  | 'seo'
  | 'content'
  | 'support'
  | 'developer-platform'
  | 'api-usage'
  | 'security'
  | 'compliance'
  | 'ai'
  | 'localization'
  | 'platform-engineering'
  | 'finops';

export interface DataDomain {
  id: DataDomainId;
  name: string;
  description: string;
  businessOwner: string;
  technicalOwner: string;
  dataSteward: string;
  assetCount: number;
  qualityScorePct: number;
  certifiedProductsCount: number;
  activeContractsCount: number;
  openIncidentsCount: number;
}

export type DataAssetType =
  | 'TABLE'
  | 'VIEW'
  | 'MATERIALIZED_VIEW'
  | 'FILE'
  | 'API_DATASET'
  | 'EVENT_DATASET'
  | 'ANALYTICS_MODEL'
  | 'METRIC'
  | 'DATA_PRODUCT'
  | 'FEATURE_VIEW'
  | 'ML_DATASET'
  | 'AI_CONTEXT_DATASET';

export type DataAssetLifecycle =
  | 'DRAFT'
  | 'EXPERIMENTAL'
  | 'ACTIVE'
  | 'CERTIFIED'
  | 'DEPRECATED'
  | 'SUNSETTING'
  | 'RETIRED';

export type DataAssetClassification =
  | 'PUBLIC'
  | 'INTERNAL'
  | 'CONFIDENTIAL'
  | 'RESTRICTED'
  | 'SECURITY_RESTRICTED';

export type DataQualityTier = 'BRONZE' | 'SILVER' | 'GOLD' | 'CRITICAL_FINANCIAL';

export interface DataFieldDefinition {
  name: string;
  logicalType: string;
  physicalType: string;
  description: string;
  semanticType:
    | 'CURRENCY_CODE'
    | 'MONEY_AMOUNT'
    | 'PERCENTAGE'
    | 'COUNTRY_CODE'
    | 'MARKET_CODE'
    | 'JURISDICTION_ID'
    | 'USER_ID'
    | 'EMAIL'
    | 'IP_ADDRESS'
    | 'TAX_RATE'
    | 'FX_RATE'
    | 'TIMESTAMP'
    | 'DATE_ONLY'
    | 'IDENTIFIER'
    | 'TEXT'
    | 'JSON_METADATA';
  nullable: boolean;
  sensitivity: 'LOW' | 'MEDIUM' | 'HIGH' | 'PII_CRITICAL';
  classification: DataAssetClassification;
  unit?: string;
  currencySemantics?: string;
  owner: string;
}

export interface DataAsset {
  id: string;
  name: string;
  type: DataAssetType;
  domain: DataDomainId;
  description: string;
  businessDefinition: string;
  technicalDefinition: string;
  owner: string;
  steward: string;
  systemOfRecord: string;
  sourceSystem: string;
  environment: 'PRODUCTION' | 'STAGING' | 'ANALYTICS_MART';
  classification: DataAssetClassification;
  sensitivity: 'NONE' | 'PII' | 'FINANCIAL' | 'SECURITY' | 'RESTRICTED_HEALTH';
  qualityTier: DataQualityTier;
  certificationStatus: 'CERTIFIED' | 'CONDITIONAL' | 'NOT_CERTIFIED';
  retentionPolicyId: string;
  accessPolicyId: string;
  contractId?: string;
  lifecycle: DataAssetLifecycle;
  fields: DataFieldDefinition[];
  freshnessStatus: 'FRESH' | 'DELAYED' | 'STALE' | 'UNKNOWN';
  lastVerifiedAt: string;
  createdAt: string;
}

// ------------------------------------------
// BUSINESS GLOSSARY & MASTER DATA
// ------------------------------------------

export interface BusinessTerm {
  id: string;
  term: string;
  definition: string;
  domain: DataDomainId;
  synonyms: string[];
  owner: string;
  approvedBy: string;
  status: 'DRAFT' | 'REVIEW' | 'APPROVED' | 'DEPRECATED';
  relatedTerms: string[];
  dataAssets: string[];
  metrics: string[];
  lastUpdated: string;
}

export interface MasterDataEntityDefinition {
  id: string;
  entityType: string;
  canonicalSource: string;
  identifierStrategy: string;
  authoritativeOwner: string;
  matchingPolicy: string;
  mergePolicy: string;
  versioningPolicy: string;
  qualityRulesCount: number;
  systemsOfConsumption: string[];
  goldenRecordStatus: 'ENFORCED' | 'PARTIAL' | 'AUDIT_ONLY';
}

export interface SystemOfRecordRegistryItem {
  entityName: string;
  systemOfRecord: string;
  systemOfEntry: string;
  systemsOfConsumption: string[];
  survivorshipPolicy: string;
  conflictResolutionRule: string;
  lastAuditDate: string;
}

// ------------------------------------------
// DATA CONTRACTS
// ------------------------------------------

export type DataContractStatus = 'DRAFT' | 'PROPOSED' | 'ACTIVE' | 'DEPRECATED' | 'VIOLATED' | 'RETIRED';

export interface DataContract {
  id: string;
  title: string;
  dataAssetId: string;
  dataAssetName: string;
  producer: string;
  consumers: string[];
  schemaVersion: string;
  semanticDefinition: string;
  qualityThresholdPct: number;
  expectedFreshnessMinutes: number;
  availabilitySLA: string;
  classification: DataAssetClassification;
  privacyRules: string;
  retentionLink: string;
  compatibilityPolicy: 'BACKWARD_COMPATIBLE' | 'MAJOR_BREAKING_BLOCKED' | 'ALIAS_REQUIRED';
  status: DataContractStatus;
  owner: string;
  effectiveFrom: string;
}

export interface DataContractViolation {
  id: string;
  contractId: string;
  dataAssetName: string;
  violationType: 'SCHEMA' | 'QUALITY' | 'FRESHNESS' | 'SEMANTIC' | 'PRIVACY' | 'AVAILABILITY' | 'COMPATIBILITY';
  severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  description: string;
  detectedAt: string;
  status: 'OPEN' | 'INVESTIGATING' | 'RESOLVED' | 'QUARANTINED';
}

// ------------------------------------------
// DATA QUALITY & OBSERVABILITY
// ------------------------------------------

export interface DataQualityRule {
  id: string;
  ruleName: string;
  assetId: string;
  ruleType: 'NOT_NULL' | 'UNIQUE' | 'RANGE' | 'ENUM' | 'REFERENTIAL' | 'FRESHNESS' | 'VOLUME' | 'DISTRIBUTION' | 'CUSTOM_SQL' | 'SEMANTIC' | 'CROSS_DATASET';
  thresholdPct: number;
  owner: string;
  status: 'PASS' | 'WARN' | 'FAIL';
  lastRunAt: string;
}

export interface DataQualityIncident {
  id: string;
  assetId: string;
  assetName: string;
  type: 'STALE_DATA' | 'VOLUME_ANOMALY' | 'NULL_SPIKE' | 'SCHEMA_DRIFT' | 'OUTLIER' | 'DUPLICATE' | 'REFERENTIAL_BREAK' | 'SEMANTIC_MISMATCH' | 'SOURCE_FAILURE';
  severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  impactDescription: string;
  affectedConsumers: string[];
  detectedAt: string;
  status: 'OPEN' | 'IN_PROGRESS' | 'RESOLVED';
  rootCause?: string;
}

export interface DataJobDefinition {
  id: string;
  jobName: string;
  jobType: 'INGEST' | 'TRANSFORM' | 'VALIDATE' | 'AGGREGATE' | 'EXPORT' | 'MATERIALIZE' | 'RETENTION' | 'FEATURE_BUILD';
  owner: string;
  schedule: string;
  codeVersion: string;
  environment: string;
  openLineageEnabled: boolean;
  lastRunStatus: 'SUCCESS' | 'FAILED' | 'RUNNING';
  lastRunAt: string;
}

// ------------------------------------------
// DATA LINEAGE GRAPH & OPENLINEAGE
// ------------------------------------------

export interface DataLineageEdge {
  id: string;
  sourceAssetId: string;
  sourceAssetName: string;
  targetAssetId: string;
  targetAssetName: string;
  edgeType: 'READS' | 'DERIVES_FROM' | 'AGGREGATES' | 'FILTERS' | 'JOINS' | 'TRANSFORMS' | 'MATERIALIZES' | 'SERVES' | 'TRAINS_ON' | 'EMBEDS_FROM';
  lineageSource: 'DECLARED' | 'OBSERVED' | 'PARSED' | 'OPENLINEAGE' | 'MANUAL';
  confidenceScore: number;
}

// ------------------------------------------
// DATA PRODUCTS & SEMANTIC LAYER
// ------------------------------------------

export interface DataProduct {
  id: string;
  name: string;
  domain: DataDomainId;
  purpose: string;
  owner: string;
  steward: string;
  containedAssets: string[];
  activeConsumers: string[];
  contractId: string;
  qualityScorePct: number;
  slo: string;
  accessPolicy: string;
  lifecycle: 'DRAFT' | 'PILOT' | 'ACTIVE' | 'CERTIFIED' | 'DEPRECATED' | 'RETIRED';
  certificationStatus: 'CERTIFIED' | 'CONDITIONAL' | 'NOT_CERTIFIED';
  readmeUrl: string;
  estimatedMonthlyCostUSD: number;
}

export interface GovernedMetricDefinition {
  id: string;
  key: string;
  name: string;
  description: string;
  businessOwner: string;
  technicalOwner: string;
  formula: string;
  grain: string;
  dimensions: string[];
  unit: 'CURRENCY' | 'PERCENTAGE' | 'COUNT' | 'DURATION' | 'RATIO';
  currencySemantics?: string;
  sourceAssets: string[];
  version: string;
  status: 'DRAFT' | 'REVIEW' | 'CERTIFIED' | 'DEPRECATED';
  effectiveFrom: string;
  lineageNodeId: string;
}

export interface SemanticModel {
  id: string;
  name: string;
  domain: DataDomainId;
  entities: string[];
  dimensions: string[];
  measures: string[];
  metrics: string[];
  owner: string;
  status: 'ACTIVE' | 'DRAFT';
}

// ------------------------------------------
// ACCESS & PRIVACY GOVERNANCE
// ------------------------------------------

export interface DataAccessPolicy {
  id: string;
  assetId: string;
  assetName: string;
  allowedRole: string;
  purpose: string;
  accessLevel: 'METADATA_ONLY' | 'AGGREGATE' | 'MASKED' | 'ROW_LEVEL' | 'FULL_RESTRICTED';
  maskingType?: 'REDACT' | 'HASH_PSEUDONYMIZE' | 'GENERALIZE' | 'BUCKET' | 'NULL' | 'TOKENIZE';
  approvalRequired: string;
  autoExpiryDays: number;
}

export interface DataAccessRequest {
  id: string;
  applicantName: string;
  applicantRole: string;
  targetAssetId: string;
  targetAssetName: string;
  purposeJustification: string;
  requestedAccessLevel: 'METADATA_ONLY' | 'AGGREGATE' | 'MASKED' | 'ROW_LEVEL' | 'FULL_RESTRICTED';
  durationDays: number;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'EXPIRED';
  requestedAt: string;
}

// ------------------------------------------
// AI DATA READINESS & ML GOVERNANCE
// ------------------------------------------

export type AIDataEligibilityState =
  | 'PROHIBITED'
  | 'REVIEW_REQUIRED'
  | 'RAG_ONLY'
  | 'EVALUATION_ONLY'
  | 'TRAINING_ALLOWED'
  | 'FEATURE_ALLOWED'
  | 'PUBLIC_CONTEXT_ALLOWED';

export interface AIDataEligibilityPolicy {
  assetId: string;
  assetName: string;
  ragAllowed: boolean;
  embeddingAllowed: boolean;
  trainingAllowed: boolean;
  evaluationAllowed: boolean;
  overallState: AIDataEligibilityState;
  justification: string;
  reviewedBy: string;
  reviewedAt: string;
}

export interface AIContextDataset {
  id: string;
  name: string;
  purpose: string;
  sourceAssets: string[];
  eligibilityState: AIDataEligibilityState;
  chunkingPolicyVersion: string;
  embeddingModel: string;
  vectorIndexName: string;
  freshnessLagMinutes: number;
  owner: string;
  qualityScorePct: number;
}

export interface FeatureDefinition {
  id: string;
  featureName: string;
  entityName: string;
  definition: string;
  sourceAsset: string;
  transformation: string;
  owner: string;
  dataType: string;
  freshnessContractMinutes: number;
  privacyClassification: DataAssetClassification;
  version: string;
  isOnlineAvailable: boolean;
  isOfflineAvailable: boolean;
}

export interface MLDatasetDefinition {
  id: string;
  datasetName: string;
  datasetType: 'TRAINING' | 'VALIDATION' | 'TEST' | 'BACKTEST' | 'SHADOW';
  version: string;
  immutableSnapshotRef: string;
  sourceFeatures: string[];
  owner: string;
  provenanceLineageId: string;
  targetLeakageChecked: boolean;
  temporalSplitEnforced: boolean;
  privacyApproved: boolean;
}

// ==========================================
// MOCK DATA GENERATION
// ==========================================

export const MOCK_DATA_DOMAINS: DataDomain[] = [
  {
    id: 'financial-market-data',
    name: 'Financial Market Data & FX',
    description: 'Taux de change officiels, paires de devises, indices de bourses mondiales et flux interbancaires.',
    businessOwner: 'Chief Investment Officer',
    technicalOwner: 'Principal Market Data Architect',
    dataSteward: 'Senior Financial Data Steward',
    assetCount: 14,
    qualityScorePct: 99.8,
    certifiedProductsCount: 3,
    activeContractsCount: 4,
    openIncidentsCount: 0
  },
  {
    id: 'transfer',
    name: 'Money Transfer & Corridors',
    description: 'Corridors de transfert transfrontaliers, frais de réseau, temps d’exécution et devises de règlement.',
    businessOwner: 'VP Global Payments',
    technicalOwner: 'Lead Payment Engineer',
    dataSteward: 'Payment Compliance Steward',
    assetCount: 18,
    qualityScorePct: 99.4,
    certifiedProductsCount: 2,
    activeContractsCount: 3,
    openIncidentsCount: 0
  },
  {
    id: 'salary-payroll',
    name: 'Salary, Payroll & Tax Rulesets',
    description: 'Barèmes fiscaux par pays, tranches d’imposition, cotisations sociales et règles salariales.',
    businessOwner: 'Head of Global Payroll',
    technicalOwner: 'Tax Engine Architect',
    dataSteward: 'International Tax Steward',
    assetCount: 22,
    qualityScorePct: 98.9,
    certifiedProductsCount: 3,
    activeContractsCount: 5,
    openIncidentsCount: 0
  },
  {
    id: 'identity',
    name: 'Identity & Auth Governance',
    description: 'Comptes utilisateurs, accréditations KYC/AML, préférences et consentement RGPD.',
    businessOwner: 'Chief Security Officer',
    technicalOwner: 'Identity Platform Lead',
    dataSteward: 'Privacy Protection Officer',
    assetCount: 12,
    qualityScorePct: 99.9,
    certifiedProductsCount: 1,
    activeContractsCount: 2,
    openIncidentsCount: 0
  },
  {
    id: 'universal-calculators',
    name: 'Universal Financial Calculators',
    description: 'Modèles de simulation financière, entrées/sorties de calculs (crédit, impôts, retraite, devises).',
    businessOwner: 'Head of Financial Products',
    technicalOwner: 'Lead Calculator Engineer',
    dataSteward: 'Product Data Steward',
    assetCount: 28,
    qualityScorePct: 97.5,
    certifiedProductsCount: 4,
    activeContractsCount: 6,
    openIncidentsCount: 1
  },
  {
    id: 'revenue',
    name: 'Revenue & Commercial Billing',
    description: 'Abonnements SaaS, commissions d’affiliation, revenus publicitaires et comptabilité MRR/ARR.',
    businessOwner: 'Chief Revenue Officer',
    technicalOwner: 'Billing System Lead',
    dataSteward: 'Revenue Operations Steward',
    assetCount: 16,
    qualityScorePct: 99.7,
    certifiedProductsCount: 2,
    activeContractsCount: 4,
    openIncidentsCount: 0
  },
  {
    id: 'ai',
    name: 'AI & Knowledge Graph Data',
    description: 'Vecteurs RAG, corpus de connaissances financières, datasets d’évaluation et prompts d’IA.',
    businessOwner: 'Chief AI Officer',
    technicalOwner: 'AI Platform Lead',
    dataSteward: 'AI Ethics & Governance Steward',
    assetCount: 15,
    qualityScorePct: 96.2,
    certifiedProductsCount: 2,
    activeContractsCount: 3,
    openIncidentsCount: 1
  }
];

export const MOCK_DATA_ASSETS: DataAsset[] = [
  {
    id: 'asset-fx-daily-rates',
    name: 'fx_daily_canonical_rates',
    type: 'TABLE',
    domain: 'financial-market-data',
    description: 'Taux de change officiels quotidiens certifiés nettoyés depuis 14 banques centrales.',
    businessDefinition: 'Source officielle de vérité pour les conversions de devises YamooPay.',
    technicalDefinition: 'PostgreSQL table public.fx_daily_rates avec contraintes d’unicité sur (base_currency, target_currency, rate_date).',
    owner: 'Chief Investment Officer',
    steward: 'Senior Financial Data Steward',
    systemOfRecord: 'ECB & Central Banks Feed Engine',
    sourceSystem: 'fx_ingest_service',
    environment: 'PRODUCTION',
    classification: 'INTERNAL',
    sensitivity: 'NONE',
    qualityTier: 'CRITICAL_FINANCIAL',
    certificationStatus: 'CERTIFIED',
    retentionPolicyId: 'ret-10y-financial',
    accessPolicyId: 'pol-internal-read',
    contractId: 'contract-fx-001',
    lifecycle: 'CERTIFIED',
    freshnessStatus: 'FRESH',
    fields: [
      { name: 'base_currency', logicalType: 'String', physicalType: 'VARCHAR(3)', description: 'Code ISO 4217 de la devise source', semanticType: 'CURRENCY_CODE', nullable: false, sensitivity: 'LOW', classification: 'INTERNAL', owner: 'Financial Data Steward' },
      { name: 'target_currency', logicalType: 'String', physicalType: 'VARCHAR(3)', description: 'Code ISO 4217 de la devise cible', semanticType: 'CURRENCY_CODE', nullable: false, sensitivity: 'LOW', classification: 'INTERNAL', owner: 'Financial Data Steward' },
      { name: 'exchange_rate', logicalType: 'Decimal', physicalType: 'NUMERIC(18,6)', description: 'Taux de conversion officiel', semanticType: 'FX_RATE', nullable: false, sensitivity: 'LOW', classification: 'INTERNAL', unit: 'ratio', currencySemantics: 'target/base', owner: 'Financial Data Steward' },
      { name: 'as_of_date', logicalType: 'Date', physicalType: 'DATE', description: 'Date d’enregistrement de la banque centrale', semanticType: 'DATE_ONLY', nullable: false, sensitivity: 'LOW', classification: 'INTERNAL', owner: 'Financial Data Steward' }
    ],
    lastVerifiedAt: '2026-08-25 04:00:00',
    createdAt: '2025-01-10 00:00:00'
  },
  {
    id: 'asset-corridor-intelligence',
    name: 'transfer_corridor_intelligence_mart',
    type: 'DATA_PRODUCT',
    domain: 'transfer',
    description: 'Data Product compilant les frais, délais moyens et marges d’échange sur 120+ corridors transfrontaliers.',
    businessDefinition: 'Agrégat d’intelligence opérationnelle pour les comparateurs de transferts d’argent.',
    technicalDefinition: 'Vue matérialisée PostgreSQL marquant les performances par couloir de transfert.',
    owner: 'VP Global Payments',
    steward: 'Payment Compliance Steward',
    systemOfRecord: 'YamooPay Corridor Analytics Engine',
    sourceSystem: 'payment_ingest_pipeline',
    environment: 'ANALYTICS_MART',
    classification: 'PUBLIC',
    sensitivity: 'NONE',
    qualityTier: 'GOLD',
    certificationStatus: 'CERTIFIED',
    retentionPolicyId: 'ret-5y-analytics',
    accessPolicyId: 'pol-public-read',
    contractId: 'contract-corridor-002',
    lifecycle: 'CERTIFIED',
    freshnessStatus: 'FRESH',
    fields: [
      { name: 'corridor_code', logicalType: 'String', physicalType: 'VARCHAR(10)', description: 'Code du couloir (ex: US-MX, FR-SN)', semanticType: 'MARKET_CODE', nullable: false, sensitivity: 'LOW', classification: 'PUBLIC', owner: 'Payment Steward' },
      { name: 'avg_fee_pct', logicalType: 'Decimal', physicalType: 'NUMERIC(6,4)', description: 'Pourcentage moyen de frais', semanticType: 'PERCENTAGE', nullable: false, sensitivity: 'LOW', classification: 'PUBLIC', unit: '%', owner: 'Payment Steward' },
      { name: 'median_speed_minutes', logicalType: 'Integer', physicalType: 'INT', description: 'Temps médian d’exécution en minutes', semanticType: 'TEXT', nullable: false, sensitivity: 'LOW', classification: 'PUBLIC', unit: 'minutes', owner: 'Payment Steward' }
    ],
    lastVerifiedAt: '2026-08-25 03:30:00',
    createdAt: '2025-03-15 00:00:00'
  },
  {
    id: 'asset-user-identity',
    name: 'user_account_master',
    type: 'TABLE',
    domain: 'identity',
    description: 'Référentiel principal des utilisateurs de YamooPay (Comptes, PII masquée).',
    businessDefinition: 'Base de données d’authentification et de profils clients.',
    technicalDefinition: 'Table PostgreSQL partitionnée identity.users.',
    owner: 'Chief Security Officer',
    steward: 'Privacy Protection Officer',
    systemOfRecord: 'YamooPay Auth Service',
    sourceSystem: 'auth_microservice',
    environment: 'PRODUCTION',
    classification: 'RESTRICTED',
    sensitivity: 'PII',
    qualityTier: 'CRITICAL_FINANCIAL',
    certificationStatus: 'CERTIFIED',
    retentionPolicyId: 'ret-gdpr-erasure-7y',
    accessPolicyId: 'pol-pii-strict-masked',
    contractId: 'contract-identity-003',
    lifecycle: 'CERTIFIED',
    freshnessStatus: 'FRESH',
    fields: [
      { name: 'user_id', logicalType: 'UUID', physicalType: 'UUID', description: 'Identifiant unique utilisateur', semanticType: 'USER_ID', nullable: false, sensitivity: 'HIGH', classification: 'RESTRICTED', owner: 'Privacy Officer' },
      { name: 'email_hash', logicalType: 'String', physicalType: 'VARCHAR(64)', description: 'Empreinte SHA-256 de l’email client', semanticType: 'EMAIL', nullable: false, sensitivity: 'HIGH', classification: 'RESTRICTED', owner: 'Privacy Officer' },
      { name: 'country_code', logicalType: 'String', physicalType: 'VARCHAR(2)', description: 'Pays de résidence principale', semanticType: 'COUNTRY_CODE', nullable: false, sensitivity: 'MEDIUM', classification: 'INTERNAL', owner: 'Privacy Officer' }
    ],
    lastVerifiedAt: '2026-08-25 04:30:00',
    createdAt: '2025-01-01 00:00:00'
  },
  {
    id: 'asset-rag-knowledge-corpus',
    name: 'ai_rag_financial_knowledge_chunks',
    type: 'AI_CONTEXT_DATASET',
    domain: 'ai',
    description: 'Corpus vectorisé des règles fiscales et bancaires mondiales certifiées pour l’assistant IA YamooPay.',
    businessDefinition: 'Base d’entraînement et de contexte RAG pour l’IA financière.',
    technicalDefinition: 'Dataset d’index vectoriel pgvector + embeddings Gemini 1.5/3.5 Flash.',
    owner: 'Chief AI Officer',
    steward: 'AI Ethics & Governance Steward',
    systemOfRecord: 'YamooPay Knowledge Ingestion Engine',
    sourceSystem: 'cms_knowledge_graph',
    environment: 'PRODUCTION',
    classification: 'INTERNAL',
    sensitivity: 'NONE',
    qualityTier: 'GOLD',
    certificationStatus: 'CERTIFIED',
    retentionPolicyId: 'ret-continuous-versioned',
    accessPolicyId: 'pol-ai-rag-only',
    lifecycle: 'CERTIFIED',
    freshnessStatus: 'FRESH',
    fields: [
      { name: 'chunk_id', logicalType: 'String', physicalType: 'VARCHAR(64)', description: 'ID unique du segment textuel', semanticType: 'IDENTIFIER', nullable: false, sensitivity: 'LOW', classification: 'INTERNAL', owner: 'AI Steward' },
      { name: 'source_asset_id', logicalType: 'String', physicalType: 'VARCHAR(64)', description: 'Lien de provenance vers l’asset source', semanticType: 'IDENTIFIER', nullable: false, sensitivity: 'LOW', classification: 'INTERNAL', owner: 'AI Steward' },
      { name: 'vector_embedding', logicalType: 'Vector', physicalType: 'VECTOR(768)', description: 'Vecteur sémantique dense', semanticType: 'TEXT', nullable: false, sensitivity: 'LOW', classification: 'INTERNAL', owner: 'AI Steward' }
    ],
    lastVerifiedAt: '2026-08-25 02:00:00',
    createdAt: '2025-05-10 00:00:00'
  }
];

export const MOCK_BUSINESS_TERMS: BusinessTerm[] = [
  {
    id: 'term-mrr',
    term: 'Monthly Recurring Revenue (MRR)',
    definition: 'Revenu récurrent mensuel normalisé généré par les abonnements payants actifs, hors frais d’installation et commissions ponctuelles.',
    domain: 'revenue',
    synonyms: ['MRR', 'Revenu Récurrent Mensuel'],
    owner: 'Chief Revenue Officer',
    approvedBy: 'Financial Governance Board',
    status: 'APPROVED',
    relatedTerms: ['ARR', 'Active Paid Subscription', 'Churn Rate'],
    dataAssets: ['asset-revenue-daily-mart'],
    metrics: ['metric-mrr-certified'],
    lastUpdated: '2026-06-01'
  },
  {
    id: 'term-active-user',
    term: 'Active User (DAU/WAU/MAU)',
    definition: 'Utilisateur unique ayant effectué au moins une action financière qualifiée (calculateur, simulation, conversion FX ou connexion authentifiée) au cours de la période.',
    domain: 'analytics',
    synonyms: ['Utilisateur Actif', 'Unique Active Visitor'],
    owner: 'VP Product Analytics',
    approvedBy: 'Data Governance Committee',
    status: 'APPROVED',
    relatedTerms: ['DAU', 'WAU', 'MAU', 'Activated User'],
    dataAssets: ['asset-analytics-user-activity'],
    metrics: ['metric-dau-certified', 'metric-mau-certified'],
    lastUpdated: '2026-07-15'
  },
  {
    id: 'term-tax-ruleset',
    term: 'Tax Ruleset Jurisdiction',
    definition: 'Ensemble de paramètres d’imposition officiels applicables à un pays/territoire pour une année fiscale donnée.',
    domain: 'salary-payroll',
    synonyms: ['Barème Fiscal', 'Ruleset Fiscal'],
    owner: 'Head of Global Payroll',
    approvedBy: 'Chief Data Officer',
    status: 'APPROVED',
    relatedTerms: ['Tax Year', 'Social Contribution Threshold'],
    dataAssets: ['asset-tax-rulesets-master'],
    metrics: ['metric-tax-calc-completion'],
    lastUpdated: '2026-04-10'
  }
];

export const MOCK_GOVERMENT_METRICS: GovernedMetricDefinition[] = [
  {
    id: 'metric-mrr-certified',
    key: 'MRR_CERTIFIED',
    name: 'Monthly Recurring Revenue (MRR)',
    description: 'Revenu mensuel récurrent officiel certifié YamooPay.',
    businessOwner: 'Chief Revenue Officer',
    technicalOwner: 'Analytics Engineering Lead',
    formula: 'SUM(subscription_fee_usd) WHERE status = "ACTIVE"',
    grain: 'Daily snapshot aggregated monthly',
    dimensions: ['Country', 'Plan Tier', 'Acquisition Channel'],
    unit: 'CURRENCY',
    currencySemantics: 'USD',
    sourceAssets: ['asset-revenue-daily-mart'],
    version: 'v2.1',
    status: 'CERTIFIED',
    effectiveFrom: '2026-01-01',
    lineageNodeId: 'node-metric-mrr'
  },
  {
    id: 'metric-mau-certified',
    key: 'MAU_CERTIFIED',
    name: 'Monthly Active Users (MAU)',
    description: 'Nombre d’utilisateurs uniques actifs sur une période glissante de 30 jours.',
    businessOwner: 'VP Product Analytics',
    technicalOwner: 'Senior Data Engineer',
    formula: 'COUNT(DISTINCT user_id) WHERE action_timestamp >= NOW() - INTERVAL "30 days"',
    grain: 'Monthly',
    dimensions: ['Market', 'Platform (Web/PWA)', 'Locale'],
    unit: 'COUNT',
    sourceAssets: ['asset-analytics-user-activity'],
    version: 'v1.4',
    status: 'CERTIFIED',
    effectiveFrom: '2025-06-01',
    lineageNodeId: 'node-metric-mau'
  },
  {
    id: 'metric-calc-completion',
    key: 'CALC_COMPLETION_RATE',
    name: 'Universal Calculator Completion Rate',
    description: 'Pourcentage de simulations financières menées jusqu’à l’affichage du résultat final.',
    businessOwner: 'Head of Financial Products',
    technicalOwner: 'Frontend Analytics Steward',
    formula: '(COUNT(completed_simulations) / COUNT(started_simulations)) * 100',
    grain: 'Daily',
    dimensions: ['Calculator Type (Salary/Loan/FX)', 'Country', 'Device'],
    unit: 'PERCENTAGE',
    sourceAssets: ['asset-calculator-events'],
    version: 'v3.0',
    status: 'CERTIFIED',
    effectiveFrom: '2026-03-01',
    lineageNodeId: 'node-metric-calc'
  }
];

export const MOCK_DATA_CONTRACTS: DataContract[] = [
  {
    id: 'contract-fx-001',
    title: 'FX Daily Canonical Rates Producer Contract',
    dataAssetId: 'asset-fx-daily-rates',
    dataAssetName: 'fx_daily_canonical_rates',
    producer: 'FX Central Bank Ingestion Worker',
    consumers: ['Universal FX Calculator', 'Money Transfer Intelligence Engine', 'Multi-Currency Wealth Mart'],
    schemaVersion: '2.0.0',
    semanticDefinition: 'Taux officiels issus directement des banques centrales à 16:00 UTC sans marge additionnelle.',
    qualityThresholdPct: 99.9,
    expectedFreshnessMinutes: 60,
    availabilitySLA: '99.99% uptime',
    classification: 'INTERNAL',
    privacyRules: 'Aucune PII. Données publiques de marché.',
    retentionLink: 'ret-10y-financial',
    compatibilityPolicy: 'MAJOR_BREAKING_BLOCKED',
    status: 'ACTIVE',
    owner: 'Chief Investment Officer',
    effectiveFrom: '2026-01-01'
  },
  {
    id: 'contract-identity-003',
    title: 'User Identity & Privacy Access Contract',
    dataAssetId: 'asset-user-identity',
    dataAssetName: 'user_account_master',
    producer: 'Auth & Identity Microservice',
    consumers: ['Analytics Data Mart (Masked Only)', 'Compliance Audit Engine'],
    schemaVersion: '1.2.0',
    semanticDefinition: 'Comptes utilisateurs avec pseudonymisation stricte des champs PII.',
    qualityThresholdPct: 100.0,
    expectedFreshnessMinutes: 5,
    availabilitySLA: '99.95% uptime',
    classification: 'RESTRICTED',
    privacyRules: 'Email et IPs hachés par défaut. Accès brut restreint aux délégués DPO.',
    retentionLink: 'ret-gdpr-erasure-7y',
    compatibilityPolicy: 'ALIAS_REQUIRED',
    status: 'ACTIVE',
    owner: 'Chief Security Officer',
    effectiveFrom: '2025-06-01'
  }
];

export const MOCK_DATA_PRODUCTS: DataProduct[] = [
  {
    id: 'prod-currency-ref',
    name: 'Canonical Currency & FX Reference Product',
    domain: 'financial-market-data',
    purpose: 'Fournir des taux de change certifiés temps réel et historiques pour toute l’application et les API externes.',
    owner: 'Chief Investment Officer',
    steward: 'Senior Financial Data Steward',
    containedAssets: ['fx_daily_canonical_rates', 'currency_metadata_master'],
    activeConsumers: ['FX Calculator', 'Transfer Engine', 'Developer API v1'],
    contractId: 'contract-fx-001',
    qualityScorePct: 99.8,
    slo: 'Disponibilité 99.99%, Fraîcheur < 60 minutes',
    accessPolicy: 'Accès Public Read pour taux agrégés, Internal pour historiques complets',
    lifecycle: 'CERTIFIED',
    certificationStatus: 'CERTIFIED',
    readmeUrl: 'https://docs.yamoopay.com/data-products/currency-ref',
    estimatedMonthlyCostUSD: 145.0
  },
  {
    id: 'prod-corridor-intelligence',
    name: 'Global Money Transfer Corridor Intelligence',
    domain: 'transfer',
    purpose: 'Fournir une cartographie précise des coûts, délais et fournisseurs sur 120+ couloirs transfrontaliers.',
    owner: 'VP Global Payments',
    steward: 'Payment Compliance Steward',
    containedAssets: ['transfer_corridor_intelligence_mart', 'provider_fee_structures'],
    activeConsumers: ['Money Transfer Comparator UI', 'Partner API', 'Executive BI Dashboard'],
    contractId: 'contract-corridor-002',
    qualityScorePct: 99.4,
    slo: 'Mise à jour quotidienne à 02:00 UTC',
    accessPolicy: 'Accès public via API sécurisée',
    lifecycle: 'CERTIFIED',
    certificationStatus: 'CERTIFIED',
    readmeUrl: 'https://docs.yamoopay.com/data-products/corridor-intelligence',
    estimatedMonthlyCostUSD: 85.0
  }
];

export const MOCK_AI_ELIGIBILITY: AIDataEligibilityPolicy[] = [
  {
    assetId: 'asset-rag-knowledge-corpus',
    assetName: 'ai_rag_financial_knowledge_chunks',
    ragAllowed: true,
    embeddingAllowed: true,
    trainingAllowed: false,
    evaluationAllowed: true,
    overallState: 'RAG_ONLY',
    justification: 'Base de connaissances réglementaires certifiée pour RAG. Entraînement interdit pour éviter la mémorisation incontrôlée.',
    reviewedBy: 'Chief AI Officer & Privacy Officer',
    reviewedAt: '2026-08-01'
  },
  {
    assetId: 'asset-user-identity',
    assetName: 'user_account_master',
    ragAllowed: false,
    embeddingAllowed: false,
    trainingAllowed: false,
    evaluationAllowed: false,
    overallState: 'PROHIBITED',
    justification: 'Données personnelles PII strictement interdites d’injection dans tout modèle IA ou RAG.',
    reviewedBy: 'Chief Security Officer',
    reviewedAt: '2026-01-10'
  },
  {
    assetId: 'asset-fx-daily-rates',
    assetName: 'fx_daily_canonical_rates',
    ragAllowed: true,
    embeddingAllowed: true,
    trainingAllowed: true,
    evaluationAllowed: true,
    overallState: 'TRAINING_ALLOWED',
    justification: 'Données de marché publiques sans risque de confidentialité.',
    reviewedBy: 'Chief Data Officer',
    reviewedAt: '2026-03-15'
  }
];

export const MOCK_FEATURE_DEFINITIONS: FeatureDefinition[] = [
  {
    id: 'feat-user-calc-frequency-30d',
    featureName: 'user_simulation_count_30d',
    entityName: 'User',
    definition: 'Nombre total de calculs financiers réalisés par un utilisateur au cours des 30 derniers jours.',
    sourceAsset: 'asset-calculator-events',
    transformation: 'COUNT(event_id) FILTER (WHERE created_at >= NOW() - INTERVAL "30 days")',
    owner: 'Senior ML Platform Engineer',
    dataType: 'INTEGER',
    freshnessContractMinutes: 1440,
    privacyClassification: 'INTERNAL',
    version: '1.0.0',
    isOnlineAvailable: true,
    isOfflineAvailable: true
  },
  {
    id: 'feat-corridor-avg-spread',
    featureName: 'corridor_fx_spread_bps',
    entityName: 'Corridor',
    definition: 'Écart moyen en points de base entre le taux interbancaire et le taux appliqué par les fournisseurs.',
    sourceAsset: 'asset-corridor-intelligence',
    transformation: '(avg_applied_rate - midmarket_rate) / midmarket_rate * 10000',
    owner: 'Principal Market Data Architect',
    dataType: 'FLOAT',
    freshnessContractMinutes: 60,
    privacyClassification: 'PUBLIC',
    version: '2.1.0',
    isOnlineAvailable: true,
    isOfflineAvailable: true
  }
];

// ==========================================
// STORE CLASS / SERVICE ENGINE
// ==========================================

class DataGovernanceStore {
  private domains: DataDomain[] = [...MOCK_DATA_DOMAINS];
  private assets: DataAsset[] = [...MOCK_DATA_ASSETS];
  private businessTerms: BusinessTerm[] = [...MOCK_BUSINESS_TERMS];
  private governedMetrics: GovernedMetricDefinition[] = [...MOCK_GOVERMENT_METRICS];
  private contracts: DataContract[] = [...MOCK_DATA_CONTRACTS];
  private products: DataProduct[] = [...MOCK_DATA_PRODUCTS];
  private aiEligibility: AIDataEligibilityPolicy[] = [...MOCK_AI_ELIGIBILITY];
  private features: FeatureDefinition[] = [...MOCK_FEATURE_DEFINITIONS];

  // Getters
  getDomains(): DataDomain[] { return this.domains; }
  getAssets(): DataAsset[] { return this.assets; }
  getBusinessTerms(): BusinessTerm[] { return this.businessTerms; }
  getMetrics(): GovernedMetricDefinition[] { return this.governedMetrics; }
  getContracts(): DataContract[] { return this.contracts; }
  getProducts(): DataProduct[] { return this.products; }
  getAIEligibility(): AIDataEligibilityPolicy[] { return this.aiEligibility; }
  getFeatures(): FeatureDefinition[] { return this.features; }

  // Search Data Catalog
  searchCatalog(query: string, domainFilter?: DataDomainId, lifecycleFilter?: string): DataAsset[] {
    const q = query.toLowerCase().trim();
    return this.assets.filter((asset) => {
      const matchQuery =
        !q ||
        asset.name.toLowerCase().includes(q) ||
        asset.description.toLowerCase().includes(q) ||
        asset.businessDefinition.toLowerCase().includes(q) ||
        asset.owner.toLowerCase().includes(q);
      const matchDomain = !domainFilter || asset.domain === domainFilter;
      const matchLifecycle = !lifecycleFilter || asset.lifecycle === lifecycleFilter;
      return matchQuery && matchDomain && matchLifecycle;
    });
  }

  // Certify Data Product
  certifyDataProduct(productId: string): boolean {
    const prod = this.products.find((p) => p.id === productId);
    if (prod) {
      prod.certificationStatus = 'CERTIFIED';
      prod.lifecycle = 'CERTIFIED';
      return true;
    }
    return false;
  }

  // Add Data Contract Violation
  reportContractViolation(contractId: string, violationType: DataContractViolation['violationType'], description: string) {
    const contract = this.contracts.find((c) => c.id === contractId);
    if (contract) {
      contract.status = 'VIOLATED';
    }
  }

  // Add Business Term
  addBusinessTerm(term: Omit<BusinessTerm, 'id' | 'lastUpdated'>) {
    const newTerm: BusinessTerm = {
      ...term,
      id: `term-${Date.now()}`,
      lastUpdated: new Date().toISOString().split('T')[0]
    };
    this.businessTerms.push(newTerm);
    return newTerm;
  }

  // Certify Metric
  certifyMetric(metricId: string) {
    const m = this.governedMetrics.find((item) => item.id === metricId);
    if (m) {
      m.status = 'CERTIFIED';
    }
  }

  // Check AI Eligibility
  getAssetAIEligibility(assetId: string): AIDataEligibilityPolicy | undefined {
    return this.aiEligibility.find((item) => item.assetId === assetId);
  }
}

export const dataGovernanceStore = new DataGovernanceStore();
